import React, { useRef, useState } from "react";
import { motion, useSpring, useTransform } from "framer-motion";
import "./verify.css";
import { Check, X } from "lucide-react";
import { useNavigate } from "react-router";

const Verify = () => {
  const navigate = useNavigate();
  const [file, setFile] = useState(null);
  const [uploadStatus, setUploadStatus] = useState("initial");
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [isUploading, setIsUploading] = useState(false);
  const uploadInterval = useRef(null);
  const abortController = useRef(null); // Add AbortController ref
  const isCanceled = useRef(false); // Add flag to track cancellation

  const API_ENDPOINT = "/wp-json/dbsb/v1/upload-document";

  // Smooth progress animation using useSpring
  const springProgress = useSpring(0, { stiffness: 100, damping: 20 });
  const displayProgress = useTransform(
    springProgress,
    (value) => `${Math.round(value)}%`
  );

  const handleFileChange = async (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      const validFormats = ["application/pdf", "image/jpeg", "image/png"];
      const maxSize = 10 * 1024 * 1024;

      if (!validFormats.includes(selectedFile.type)) {
        setError("File format not supported");
        setUploadStatus("failed");
        setFile(selectedFile);
        return;
      }

      if (selectedFile.size > maxSize) {
        setError("File size exceeds 10MB limit");
        setUploadStatus("failed");
        setFile(selectedFile);
        return;
      }

      setFile(selectedFile);
      setUploadStatus("uploading");
      setError("");
      setSuccess("");
      setIsUploading(true);
      isCanceled.current = false; // Reset cancellation flag
      abortController.current = new AbortController(); // Initialize AbortController

      let currentProgress = 0;

      // Animate progress smoothly up to 90%
      const animateProgress = () => {
        if (currentProgress < 90) {
          currentProgress = Math.min(currentProgress + 2, 90); // Increment by 2% per frame
          setProgress(currentProgress);
          springProgress.set(currentProgress);
          uploadInterval.current = setTimeout(animateProgress, 100); // Update every 100ms for smoothness
        }
      };

      animateProgress();

      const formData = new FormData();
      formData.append("medical_license", selectedFile);

      console.log("File object:", selectedFile);
      console.log("File name:", selectedFile.name);
      console.log("File type:", selectedFile.type);
      console.log("File size:", selectedFile.size);

      for (let pair of formData.entries()) {
        console.log(
          pair[0] + ": " + (pair[1] instanceof File ? pair[1].name : pair[1])
        );
      }

      try {
        // Check for nonce
        if (!window.wpApiSettings?.nonce) {
          throw new Error(
            "Authentication token not found. Please try logging in again."
          );
        }

        console.log("About to send request with formData");
        const response = await fetch(API_ENDPOINT, {
          method: "POST",
          body: formData,
          credentials: "include",
          headers: {
            "X-WP-Nonce": window.wpApiSettings.nonce, // Add the nonce header
          },
          signal: abortController.current.signal, // Attach the abort signal
        });
        console.log("Request sent, status:", response.status);

        console.log("Upload response status:", response.status);

        const data = await response.json();

        console.log("Upload response data:", data);

        if (!response.ok) {
          if (response.status === 401) {
            throw new Error(
              "You must be logged in to upload a document. Please log in and try again."
            );
          } else if (response.status === 403) {
            throw new Error("You don't have permission to upload a document.");
          } else {
            throw new Error(data.error || "Upload failed");
          }
        }

        // Only update state if the upload wasn't canceled
        if (!isCanceled.current) {
          clearTimeout(uploadInterval.current);
          uploadInterval.current = null;
          setProgress(100);
          springProgress.set(100);
          setUploadStatus("complete");
          setSuccess("Document uploaded successfully!");
        }
      } catch (error) {
        // Ignore the error if the request was aborted
        if (error.name === "AbortError") {
          return; // Request was canceled, do nothing
        }

        // Only update state if the upload wasn't canceled
        if (!isCanceled.current) {
          clearTimeout(uploadInterval.current);
          uploadInterval.current = null;
          setUploadStatus("failed");
          setError(error.message);
          setProgress(0);
          springProgress.set(0);
        }
      } finally {
        // Only update isUploading if the upload wasn't canceled
        if (!isCanceled.current) {
          setIsUploading(false);
        }
      }
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) {
      const fileEvent = { target: { files: [droppedFile] } };
      handleFileChange(fileEvent);
    }
  };

  const handleUploadAnother = () => {
    setFile(null);
    setUploadStatus("initial");
    setError("");
    setSuccess("");
    setProgress(0);
    setIsUploading(false);
    if (uploadInterval.current) {
      clearTimeout(uploadInterval.current);
      uploadInterval.current = null;
    }
  };

  const handleCancelUpload = () => {
    if (uploadInterval.current) {
      clearTimeout(uploadInterval.current);
      uploadInterval.current = null;
    }

    // Mark the upload as canceled
    isCanceled.current = true;

    // Abort the fetch request
    if (abortController.current) {
      abortController.current.abort();
    }

    // Reset the UI state
    setUploadStatus("initial");
    setFile(null);
    setProgress(0);
    springProgress.set(0);
    setError("");
    setSuccess("");
    setIsUploading(false);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (uploadStatus === "complete") {
      console.log("Account creation submitted with file:", file.name);
      window.location.href = "/dashboard";
    }
  };

  const containerVariants = {
    hidden: { opacity: 0, y: -50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: "easeOut" },
    },
  };

  const formVariants = {
    hidden: { opacity: 0, x: -100 },
    visible: {
      opacity: 1,
      x: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut",
        staggerChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20, scale: 0.95 },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: { duration: 0.5, ease: [0.6, -0.05, 0.01, 0.99] },
    },
  };

  const uploadAreaVariants = {
    hidden: {
      opacity: 0,
      scale: 0.9,
      backgroundColor: "#f5f5f7",
      backgroundImage: undefined,
    },
    visible: {
      opacity: 1,
      scale: 1,
      backgroundColor: "#f5f5f7",
      backgroundImage: undefined,
      transition: { duration: 0.5, ease: "easeOut" },
    },
    uploading: {
      opacity: 1,
      scale: 1,
      backgroundColor: "#f5f5f7",
      backgroundImage: undefined,
      transition: { duration: 0.3, ease: "easeInOut" },
    },
    complete: {
      opacity: 1,
      scale: 1.02,
      backgroundImage:
        "linear-gradient(260.26deg, rgba(47, 64, 211, 0.07) 12.78%, rgba(214, 215, 225, 0.07) 118.25%)",
      backgroundColor: "transparent",
      transition: { duration: 0.4, ease: "easeOut" },
    },
    failed: {
      opacity: 1,
      scale: 0.98,
      backgroundColor: "#f5f5f7",
      backgroundImage: undefined,
      transition: { duration: 0.4, ease: "easeIn" },
    },
  };

  const buttonVariants = {
    hover: {
      scale: 1.02,
      backgroundColor: "#d1d9e6",
      transition: { duration: 0.2 },
    },
    tap: { scale: 0.98 },
    disabled: {
      scale: 1,
      transition: { duration: 0.1 },
    },
  };

  return (
    <motion.div
      className="verify-container"
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      <motion.div className="verify-card" variants={containerVariants}>
        <div className="logo">
          <a href="/">
            <img
              src={`${process.env.PUBLIC_URL}/assets/logo.svg`}
              alt="Direct by Syndicate Bio"
            />
          </a>
        </div>
        <motion.div
          className="verify-content"
          variants={formVariants}
          initial="hidden"
          animate="visible"
        >
          <h2>Verify your identity license for verification.</h2>
          <p>Upload your medical license for verification.</p>
          <motion.form
            onSubmit={handleSubmit}
            className="verify-form"
            variants={formVariants}
            initial="hidden"
            animate="visible"
            onDragOver={handleDragOver}
            onDrop={handleDrop}
          >
            <motion.div
              key={uploadStatus}
              className={`upload-area ${
                uploadStatus === "complete" ? "upload-complete" : ""
              }`}
              variants={uploadAreaVariants}
              initial="hidden"
              animate={
                uploadStatus === "uploading"
                  ? "uploading"
                  : uploadStatus === "complete"
                  ? "complete"
                  : uploadStatus === "failed"
                  ? "failed"
                  : "visible"
              }
            >
              {uploadStatus === "initial" && (
                <>
                  <img
                    src={`${process.env.PUBLIC_URL}/assets/folder-open.svg`}
                    className="file-icon"
                    alt=""
                  />
                  <p>Drag & drop your file here</p>
                  <div className="or-line">
                    <ul></ul>
                    <p>OR</p>
                    <ul></ul>
                  </div>
                  <label className="browse-button">
                    Browse files
                    <input
                      type="file"
                      onChange={handleFileChange}
                      accept=".pdf,.jpg,.png"
                      style={{ display: "none" }}
                      disabled={isUploading}
                    />
                  </label>
                </>
              )}
              {uploadStatus === "uploading" && (
                <>
                  <motion.div
                    className="progress-bar"
                    style={{ width: displayProgress }}
                  />
                  <img
                    src={`${process.env.PUBLIC_URL}/assets/folder-close.svg`}
                    className="file-icon"
                    alt=""
                  />
                  <p>{file?.name}</p>
                  <div className="upload-status">
                    <span>{Math.round(progress)}%</span>
                    <span>Uploading</span>
                    <span className="dot"></span>
                    <span className="file-size">
                      {((file?.size * progress) / 100 / 1024 / 1024).toFixed(2)}
                      MB
                    </span>
                  </div>
                  <button
                    className="cancel-button"
                    onClick={handleCancelUpload}
                    disabled={!isUploading}
                  >
                    Cancel
                  </button>
                </>
              )}
              {uploadStatus === "complete" && (
                <>
                  <img
                    src={`${process.env.PUBLIC_URL}/assets/folder-close.svg`}
                    className="file-icon"
                    alt=""
                  />
                  <p>{file?.name}</p>
                  <p className="upload-status-message-success">
                    <Check size={16} className="status-icon" /> Upload complete
                  </p>
                  <button
                    className="upload-another-button"
                    onClick={handleUploadAnother}
                  >
                    Upload another file
                  </button>
                </>
              )}
              {uploadStatus === "failed" && (
                <>
                  <img
                    src={`${process.env.PUBLIC_URL}/assets/folder-close.svg`}
                    className="file-icon"
                    alt=""
                  />
                  <p>{file?.name}</p>
                  <p className="upload-status-message-failed">
                    <X size={16} className="status-icon" /> {error}
                  </p>
                  <button
                    className="upload-another-button"
                    onClick={handleUploadAnother}
                  >
                    Upload another file
                  </button>
                </>
              )}
            </motion.div>
            {error && uploadStatus !== "failed" && (
              <span className="error">{error}</span>
            )}
            {/* {success && <span className="success-message">{success}</span>} */}
            <div className="file-types-container">
              <div className="file-type-box">PDF</div>
              <div className="file-type-box">JPG</div>
              <div className="file-type-box">PNG</div>
              <div className="file-type-box">&lt; 10MB</div>
            </div>
            <motion.div variants={itemVariants}>
              <motion.button
                type="submit"
                className={`create-account-button ${
                  isUploading ||
                  ["initial", "failed", "uploading"].includes(uploadStatus)
                    ? "disabled"
                    : ""
                }`}
                disabled={
                  isUploading ||
                  ["initial", "failed", "uploading"].includes(uploadStatus)
                }
                variants={buttonVariants}
                whileHover="hover"
                whileTap={uploadStatus === "complete" ? "tap" : "disabled"}
              >
                Create account
              </motion.button>
            </motion.div>
          </motion.form>
        </motion.div>
      </motion.div>
    </motion.div>
  );
};

export default Verify;
